<header>
    <div class="vc-flex gap-8">
        <mat-icon svgIcon="vc-logo" class="vc-header-logo"></mat-icon>
        @if (isImpersonating) {
            <vc-badge status="neutral" [showTooltip]="false" label="Admin"></vc-badge>
        }
    </div>

    @if (isImpersonating) {
        <div class="vc-header-impersonation-container">
            <div class="vc-header-impersonation-container-impersonating">
                <span class="accent-text">Impersonating</span>
                <vc-tooltip-text
                    class="body-small"
                    style="max-width: 100%"
                    [text]="isImpersonating"
                    [showTooltip]="true"></vc-tooltip-text>
            </div>
            <vc-button
                mode="error"
                size="small"
                (trigger)="endImpersonation.emit()"
                label="End"
                iconPrefix="close"></vc-button>
        </div>
    }
    <div class="vc-header-actions">
        <vc-button
            mode="icon-plain"
            iconName="notifications"
            ariaLabel="Notifications Menu"
            [tooltip]="'CORE.NOTIFICATIONS' | transloco"
            [matMenuTriggerFor]="notificationsMenu"></vc-button>
        <mat-menu #notificationsMenu="matMenu" class="vc-notifications-menu">
            <button
                mat-menu-item
                class="vc-notification-item vc-notification-header settings vc-header-menu-focus"
                (click)="navigateToNotificationSettings()">
                <span class="vc-notification-item-label" [class.h2-heading]="notifications.length > 0"
                    >Notifications</span
                >
                <mat-icon class="vc-notification-icon" aria-hidden="true">settings</mat-icon>
            </button>
            @for (notification of notifications | slice: 0 : 5; track notification) {
                <button
                    mat-menu-item
                    class="vc-notification-history-item vc-header-menu-focus"
                    [attr.aria-label]="notification.shortSubject + '' + notification.shortDescription"
                    (click)="navigateToNotificationHistory(notification.eventKey)">
                    <span class="vc-notification-history-content">
                        <mat-icon class="vc-notification-history-icon" aria-hidden="true">
                            {{ notification.eventKey | notificationEventIcon }}
                        </mat-icon>
                        <span class="vc-notification-history-text">
                            <span class="vc-flex-baseline gap-8">
                                <span class="vc-notification-history-subject label-large">
                                    {{ notification.shortSubject }}</span
                                >
                                <span class="vc-notification-history-date body-small">
                                    {{ notification.createdInstant | date: dateFormats.SHORT_DATE_ISO }}
                                </span>
                            </span>
                            <span class="vc-notification-history-description body-medium">
                                {{ notification.shortDescription }}
                            </span>
                        </span>
                    </span>
                </button>
            }
            @if (notifications.length > 0) {
                <button
                    mat-menu-item
                    class="vc-notification-history-show-all label-medium vc-header-menu-focus"
                    (click)="navigateToNotificationHistory()">
                    <span class="vc-notification-history-label label-medium">Show all notifications</span>
                </button>
            }
        </mat-menu>

        <button
            class="vc-header-action-button vc-header-menu-button-focus"
            aria-label="Language Menu"
            [matTooltip]="'CORE.LANGUAGE' | transloco"
            [matMenuTriggerFor]="langMenu">
            <img class="vc-header-flag-img" alt="Country flag" [ngSrc]="lang | countryImage" height="20" width="20" />
        </button>
        <mat-menu #langMenu="matMenu">
            @for (value of langOptions; track value) {
                <button
                    mat-menu-item
                    class="vc-lang-menu-item vc-header-menu-focus"
                    [class.highlight]="lang === value"
                    (click)="changeLanguage(value)">
                    <img alt="Country flag" [ngSrc]="value | countryImage" height="20" width="20" />
                    <span style="width: 100%">{{ value | countryLabel }}</span>
                </button>
            }
        </mat-menu>

        <vc-theme-toggle [mode]="themeMode" (modeChanged)="changeThemeMode($event)"></vc-theme-toggle>

        <vc-button
            mode="icon-plain"
            iconName="account_circle"
            ariaLabel="Account Menu"
            [tooltip]="'CORE.USER.ACCOUNT' | transloco"
            [matMenuTriggerFor]="userMenu"></vc-button>
        <mat-menu #userMenu="matMenu">
            <span
                class="vc-user-menu-username body-medium"
                (click)="$event.stopPropagation()"
                (keydown)="$event.stopPropagation()">
                {{ userName }}
            </span>
            @if (hasChildren || isOrgAdmin) {
                <button
                    mat-menu-item
                    class="vc-user-menu-item body-medium org-change vc-header-menu-focus"
                    [attr.aria-label]="selectedOrgName + ' - Change organization'"
                    [disableRipple]="true"
                    (click)="openOrgDialog()">
                    <div class="vc-header-account-menu">
                        <span
                            class="vc-user-menu-item-title org-title"
                            (click)="$event.stopPropagation()"
                            (keydown)="$event.stopPropagation()">
                            {{ selectedOrgName }}
                        </span>
                        @if (billingIds) {
                            <div
                                class="vc-org-billing-ids space-bottom-4"
                                (click)="$event.stopPropagation()"
                                (keydown)="$event.stopPropagation()">
                                <div>{{ 'CORE.ORGANIZATION.BILLING_ID' | transloco }}</div>
                                <vc-tooltip-text
                                    [text]="billingIds"
                                    class="space-left-4 space-right-16"></vc-tooltip-text>
                            </div>
                        }
                        <span class="vc-header-change-org-button">{{
                            'CORE.USER.CHANGE_ORGANIZATION' | transloco
                        }}</span>
                    </div>
                </button>
            }
            <hr class="vc-user-menu-divider" />
            @if (isOrgAdmin) {
                <button
                    mat-menu-item
                    class="vc-user-menu-item body-medium vc-header-menu-focus"
                    (click)="navigateToOrganization()">
                    <span class="body-medium">{{ 'COMMON_UI.ORGANIZATION' | transloco }}</span>
                    <mat-icon class="vc-user-menu-item-icon" aria-hidden="true">domain</mat-icon>
                </button>
            }
            <button
                mat-menu-item
                class="vc-user-menu-item body-medium vc-header-menu-focus"
                (click)="navigateToAccount()">
                <span class="vc-user-menu-item-title">{{ 'CORE.USER.ACCOUNT' | transloco }}</span>
                <mat-icon class="vc-user-menu-item-icon" aria-hidden="true">tune</mat-icon>
            </button>
            <button mat-menu-item class="vc-user-menu-item body-medium vc-header-menu-focus" (click)="logout.emit()">
                <span class="vc-user-menu-item-title">{{ 'CORE.USER.LOGOUT' | transloco }}</span>
                <mat-icon class="vc-user-menu-item-icon" aria-hidden="true">input</mat-icon>
            </button>
        </mat-menu>

        <vc-org-picker
            [(visible)]="orgDialogVisible"
            [user]="user"
            [selectedOrg]="userOrg"
            [orgModels]="orgModels"
            (selectedOrgChange)="changeOrg.emit($event.id)">
        </vc-org-picker>
    </div>
</header>
